import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { fetchAuthSession } from 'aws-amplify/auth';

export default function FileUploadComponent() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0); // overall %
  const [message, setMessage] = useState('');

  const onDrop = useCallback((acceptedFiles) => {
    setFiles((currentFiles) => [...currentFiles, ...acceptedFiles]);
  }, []);

  const removeFile = (file) => {
    setFiles((currentFiles) => currentFiles.filter((item) => item !== file));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  async function authHeaders() {
    const { tokens } = await fetchAuthSession();
    const jwt =
      tokens?.idToken?.toString?.() ??
      tokens?.accessToken?.toString?.();
    return jwt ? { Authorization: `Bearer ${jwt}` } : {};
  }

  // Request a presigned URL from your API (Cognito-protected), then PUT to S3 with progress
  const uploadFile = async (file, onProgress) => {
    const fileType = file.type?.startsWith('image/') ? 'image' : 'document';
    const apiUrl = 'https://2af2e5kjj9.execute-api.us-east-1.amazonaws.com/prod/image-upload';

    const jsonPayload = JSON.stringify({
      fileType,
      objectKey: file.name,        // keep as-is; change if backend expects a path/prefix
      contentType: file.type || 'application/octet-stream',
    });

    // 1) Get presigned URL (authorized)
    const presignRes = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(await authHeaders()),
      },
      body: jsonPayload,
    });

    const text = await presignRes.text();
    if (!text) throw new Error('Presign: empty response');
    let payload;
    try {
      payload = JSON.parse(text);
    } catch {
      throw new Error('Presign: invalid JSON response');
    }
    if (!presignRes.ok) {
      throw new Error(`Presign failed (${presignRes.status}): ${payload?.message || presignRes.statusText}`);
    }

    const { presignedUrl } = payload || {};
    if (!presignedUrl) throw new Error('Presign: missing presignedUrl');

    // 2) PUT to S3 with progress (S3 PUT usually returns empty body on success)
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      // Optional caching headers (safe defaults)
      // xhr.setRequestHeader('Cache-Control', 'max-age=31536000, immutable');

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && typeof onProgress === 'function') {
          onProgress(evt.loaded, evt.total);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`S3 upload failed: HTTP ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Network error during S3 upload'));
      xhr.send(file);
    });

    // Return a simple success marker (S3 PUT returns no JSON)
    return { ok: true, key: file.name };
  };

  const uploadFiles = async () => {
    if (!files.length) return;

    setUploading(true);
    setMessage('');
    setProgress(0);

    // Track aggregate progress across N files
    const perFileProgress = new Array(files.length).fill(0);
    const updateOverall = () => {
      const sum = perFileProgress.reduce((a, b) => a + b, 0);
      setProgress(Math.round((sum / files.length) * 100));
    };

    try {
      const uploads = files.map((file, idx) =>
        uploadFile(file, (loaded, total) => {
          const pct = total > 0 ? (loaded / total) * 100 : 0;
          perFileProgress[idx] = pct;
          updateOverall();
        })
      );

      const results = await Promise.all(uploads);
      results.forEach((r) => console.log('Upload result:', r));
      setMessage('All files uploaded successfully!');
    } catch (err) {
      console.error('Uploading error:', err);
      setMessage(`Error during file upload: ${err.message || err}`);
    } finally {
      setUploading(false);
      setFiles([]);
      setProgress(0);
    }
  };

  return (
    <div className="container listDiv">
      <div {...getRootProps()} className="dropzone">
        <input {...getInputProps()} />
        <p>Drag &apos;n&apos; drop some files here, or click to select files</p>
      </div>

      <ul>
        {files.map((file, index) => (
          <li key={`${file.name}-${index}`}>
            {file.name}
            <button type="button" onClick={() => removeFile(file)} disabled={uploading}>
              Remove
            </button>
          </li>
        ))}
      </ul>

      <button onClick={uploadFiles} disabled={uploading || files.length === 0}>
        {uploading ? 'Uploading…' : 'Upload Files'}
      </button>

      {uploading && (
        <div className="progress-bar" style={{ width: `${progress}%`, transition: 'width .2s' }}>
          {progress}%
        </div>
      )}
      {message && <p className="flash-message">{message}</p>}
    </div>
  );
}
